import React, { useState, useEffect } from 'react';
import { Users, UserPlus,
  Calendar, Clock, Phone, Mail, MapPin, 
  User, Shield, Award, CheckCircle, Zap, Target, 
  TrendingUp, Star, Crown, Sparkles, Home, Send, ArrowRight, BookOpen,
  Brain, Globe, Lock, FileText, Scale, Building, Gavel, Heart,
  Baby, UserCheck, Users as FamilyMembers, Shield as Protection
} from 'lucide-react';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ConsultationBooking = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [visibleSections, setVisibleSections] = useState({
    hero: false,
    contact: false,
    info: false
  });

  useEffect(() => {
    setIsVisible(true);
    
    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.getAttribute('data-section');
          if (sectionId) {
            setVisibleSections(prev => ({
              ...prev,
              [sectionId]: true
            }));
          }
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    // Observe all sections
    const sections = document.querySelectorAll('[data-section]');
    sections.forEach(section => observer.observe(section));

    return () => {
      sections.forEach(section => observer.unobserve(section));
    };
  }, []);

  const whatsappUrl = "https://wa.me/966115115945";

  const contactInfo = [
    {
      icon: Phone,
      title: "الرقم الموحد",
      value: "920033053",
      description: "اتصل بنا مباشرة"
    },
    {
      icon: Mail,
      title: "البريد الإلكتروني",
      value: "info@khayat.sa",
      description: "راسلنا عبر البريد الإلكتروني"
    },
    {
      icon: MapPin,
      title: "العنوان",
      value: "المملكة العربية السعودية - مدينة جدة - حي المرجان - مركز النايفة -الدور الأول - مكتب رقم 8",
      description: "مكتبنا الرئيسي"
    },
    {
      icon: Clock,
      title: "ساعات العمل",
      value: "الأحد - الخميس: 8:00 ص - 6:00 م",
      description: "متاحون لخدمتك"
    }
  ];

  const contactFeatures = [
    "استجابة سريعة لجميع الاستفسارات",
    "استشارات قانونية مجانية أولية",
    "مواعيد مرنة تناسب جدولك",
    "حجز مواعيد عبر الإنترنت",
    "متابعة مستمرة لحالة طلبك",
    "خدمة عملاء متخصصة ومحترفة"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100" dir="rtl">
      {/* Header */}
      <Header />
      
      {/* Hero Section */}
      <div 
        data-section="hero"
        className={`relative py-12 sm:py-24 overflow-hidden transition-all duration-1000 mt-16 sm:mt-20 ${visibleSections.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        style={{ backgroundColor: '#0a3a23' }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='7' cy='7' r='5'/%3E%3Ccircle cx='53' cy='7' r='5'/%3E%3Ccircle cx='30' cy='30' r='5'/%3E%3Ccircle cx='7' cy='53' r='5'/%3E%3Ccircle cx='53' cy='53' r='5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
        
        <div className={`relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center text-white transition-all duration-1000 delay-300 ${visibleSections.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
            <span className="text-white">تواصل معنا</span>
            <div className="w-20 sm:w-32 h-1 bg-white mx-auto mt-2 sm:mt-4 animate-pulse"></div>
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl opacity-90 max-w-2xl sm:max-w-4xl mx-auto leading-relaxed mb-4 sm:mb-8 px-4">
            نحن هنا لمساعدتك في جميع احتياجاتك القانونية
          </p>
          <p className="text-sm sm:text-base md:text-lg opacity-80 max-w-xl sm:max-w-3xl mx-auto px-4">
            تواصل معنا الآن للحصول على استشارة قانونية مجانية
          </p>
        </div>

        {/* Navigation Breadcrumb */}
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-1 sm:gap-2 text-white text-xs sm:text-sm">
          <Link to="/" className="flex items-center gap-1 hover:text-gray-200 transition-colors">
            <Home className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>الرئيسية</span>
          </Link>
          <span>/</span>
          <div className="flex items-center gap-1">
            <WhatsAppIcon className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>تواصل معنا</span>
          </div>
        </div>
      </div>

      {/* How it works Section */}
      <div data-section="contact" className="py-12 sm:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            
            {/* How it works */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">كيف تعمل الاستشارة؟</h2>
              <div className="grid md:grid-cols-3 gap-6">
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#0a3a23]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <WhatsAppIcon className="w-8 h-8 text-[#0a3a23]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">1. تواصل معنا</h3>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-[#0a3a23]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-[#0a3a23]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">2. حدد الموعد</h3>
                </div>
                  
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#0a3a23]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-[#0a3a23]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">3. ابدأ الاستشارة</h3>
                </div>

              </div>
            </div>

            {/* Working Hours */}
            <div className="bg-gradient-to-br from-[#0a3a23]/10 to-[#0a3a23]/5 rounded-xl p-6 mb-8 border border-[#0a3a23]/20">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-6 h-6 text-[#0a3a23]" />
                <h3 className="text-xl font-bold text-gray-900">ساعات العمل والاستجابة</h3>
              </div>

              <div className="space-y-6">
                {/* Working Hours Section */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 text-lg">أوقات العمل</h4>
                  <div className="bg-white rounded-lg p-4 space-y-2">
                    <div className="flex justify-between items-center py-2 border-b border-[#0a3a23]/20">
                      <span className="text-gray-700">السبت - الخميس</span>
                      <span className="font-semibold text-[#0a3a23]">8:00 ص - 6:00 م</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-700">الجمعة</span>
                      <span className="font-semibold text-gray-500">مغلق</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="text-center">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] hover:from-[#0a3a23]/90 hover:to-[#0a3a23]/90 text-white px-10 py-4 rounded-xl text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <WhatsAppIcon className="w-6 h-6" />
                احجز استشارتك الآن عبر الواتساب
              </a>
              <p className="text-gray-500 text-sm mt-4">
                الاستشارة متخصصة ومهنية ولا تتطلب أي التزام
              </p>
            </div>

          </div>
        </div>
      </div>

      {/* Contact Info Section */}
      <div className="py-12 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 sm:mb-8">
              معلومات التواصل
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-8 sm:mb-12">
              يمكنك التواصل معنا عبر أي من الطرق التالية
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-16">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <div key={index} className="flex items-start gap-4 p-6 rounded-xl" style={{ backgroundColor: 'rgba(10, 58, 35, 0.1)' }}>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0a3a23' }}>
                    <info.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{info.title}</h3>
                    <p className="text-lg text-gray-700 mb-1">{info.value}</p>
                    <p className="text-sm text-gray-600">{info.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact CTAs */}
            <div className="space-y-6">
              {/* Phone CTA */}
              <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(10, 58, 35, 0.1)' }}>
                <h3 className="text-xl font-bold text-gray-900 mb-4">اتصل بنا مباشرة</h3>
                <p className="text-gray-600 mb-4">للحصول على استجابة فورية، اتصل بنا على الرقم الموحد</p>
                <a
                  href="tel:920033053"
                  className="inline-flex items-center gap-3 text-white px-6 py-3 rounded-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                  style={{ backgroundColor: '#0a3a23' }}
                >
                  <Phone className="w-5 h-5" />
                  920033053
                </a>
              </div>

              {/* WhatsApp CTA */}
              <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(10, 58, 35, 0.1)' }}>
                <h3 className="text-xl font-bold text-gray-900 mb-4">تواصل فوري عبر الواتساب</h3>
                <p className="text-gray-600 mb-4">للحصول على استجابة سريعة، تواصل معنا عبر الواتساب</p>
                <a
                  href={`${whatsappUrl}?text=أريد استشارة قانونية`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 text-white px-6 py-3 rounded-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                  style={{ backgroundColor: '#0a3a23' }}
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  تواصل عبر الواتساب
                </a>
              </div>

              {/* Google Maps CTA */}
              <div className="p-6 rounded-xl" style={{ backgroundColor: 'rgba(10, 58, 35, 0.1)' }}>
                <h3 className="text-xl font-bold text-gray-900 mb-4">موقعنا</h3>
                <p className="text-gray-600 mb-4">يمكنك زيارة مكتبنا في مدينة جدة</p>
                <a
                  href="https://www.google.com.sa/maps/place/%D8%B4%D8%B1%D9%83%D8%A9+%D8%A7%D9%86%D8%B3+%D8%B9%D8%A8%D8%AF%D8%A7%D9%84%D8%B1%D8%AD%D9%85%D9%86+%D8%AE%D9%8A%D8%A7%D8%B7+%D9%84%D9%84%D9%85%D8%AD%D8%A7%D9%85%D8%A7%D8%A9+%D9%88%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D8%B4%D8%A7%D8%B1%D8%A7%D8%AA+%D8%A7%D9%84%D9%82%D8%A7%D9%86%D9%88%D9%86%D9%8A%D8%A9%E2%80%AD/@21.7015558,39.1072042,17z/data=!4m6!3m5!1s0x15c3d9df5e180a41:0x9c90f7f9629941c9!8m2!3d21.7015558!4d39.1046293!16s%2Fg%2F11ydmz6jsm?hl=ar&entry=ttu&g_ep=EgoyMDI1MDgyNS4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 text-white px-6 py-3 rounded-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                  style={{ backgroundColor: '#0a3a23' }}
                >
                  <MapPin className="w-5 h-5" />
                  موقعنا على الخريطة
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] py-16">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <Calendar className="w-16 h-16 text-white mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-6">احجز استشارتك الآن</h2>
          <p className="text-xl text-white/90 mb-8">
            احصل على استشارة قانونية متخصصة من فريق من الخبراء المعتمدين
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://wa.me/966115115945?text=أريد حجز استشارة قانونية"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#0a3a23] text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#0a3a23]/90 transition-colors shadow-lg hover:shadow-xl"
            >
              <WhatsAppIcon className="w-6 h-6" />
              احجز عبر الواتساب
            </a>
            
            <a
              href="tel:920033053"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-lg border-2 border-white/30 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/20 transition-all duration-300"
            >
              <Phone className="w-6 h-6" />
              اتصل بنا مباشرة
            </a>
          </div>
        </div>
      </div>

      {/* Features */}
      <div data-section="info" className="py-12 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${visibleSections.info ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              مميزات التواصل معنا
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto px-4">
              نتميز بخدمة عملاء متميزة واستجابة سريعة
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {contactFeatures.map((feature, index) => (
              <div 
                key={index} 
                className={`flex items-start gap-3 sm:gap-4 p-4 sm:p-6 rounded-xl transition-all duration-500 transform hover:scale-105 ${visibleSections.info ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms`, backgroundColor: 'rgba(10, 58, 35, 0.1)' }}
              >
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 mt-1 flex-shrink-0" style={{ color: '#0a3a23' }} />
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </div>



      {/* Floating Contact Buttons */}
      <a
        href="tel:920033053"
        className="fixed bottom-20 sm:bottom-28 left-4 sm:left-6 bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-[#0a3a23]/25 transition-all duration-300 z-50 hover:scale-110"
      >
        <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
      </a>

      {/* Floating WhatsApp Button */}
      <a
        href={`${whatsappUrl}?text=أريد استشارة قانونية`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 sm:bottom-6 left-4 sm:left-6 bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-[#0a3a23]/25 transition-all duration-300 z-50 hover:scale-110"
      >
        <WhatsAppIcon className="w-5 h-5 sm:w-6 sm:h-6" />
      </a>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ConsultationBooking; 
